import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Check, Eye, EyeOff, Lock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { GroomingProLogo } from '@/components/layout/GroomingProLogo';

/**
 * Donde aterriza quien pulsa el enlace del correo de recuperación.
 *
 * Cómo llega: Supabase pone el token en el FRAGMENTO de la URL (después del
 * `#`), no en la query. El cliente lo lee al cargar y deja una sesión
 * temporal con permiso para una sola cosa: cambiar la contraseña.
 *
 * Por eso esta pantalla va FUERA del layout de la aplicación: quien llega
 * aquí no ha iniciado sesión de verdad, y montar el layout dispararía sus
 * consultas y la comprobación de suscripción sin empresa ni sede.
 *
 * El patrón viene de CalzadoPro, donde ya está en producción.
 */

/** Mínimo de la contraseña. Lo mismo que exige Supabase, dicho en español. */
const MINIMO = 8;

type Estado = 'comprobando' | 'listo' | 'sin-sesion' | 'guardado';

export function RestablecerPage() {
  const [estado, setEstado] = useState<Estado>('comprobando');
  const [password, setPassword] = useState('');
  const [repetida, setRepetida] = useState('');
  const [ver, setVer] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const navigate = useNavigate();

  /**
   * ¿Trae el enlace una sesión válida?
   *
   * El cliente procesa el fragmento de forma ASÍNCRONA al cargar, así que no
   * basta con mirar la sesión una vez: hay que escuchar el evento. Sin esto,
   * un enlace bueno mostraría «enlace caducado» por llegar medio segundo
   * antes de tiempo.
   */
  useEffect(() => {
    let vivo = true;

    const { data: sub } = supabase.auth.onAuthStateChange((evento, sesion) => {
      if (!vivo) return;
      if (evento === 'PASSWORD_RECOVERY' || sesion) setEstado('listo');
    });

    // Por si el evento ya pasó antes de que este componente se montara.
    void supabase.auth.getSession().then(({ data }) => {
      if (!vivo) return;
      if (data.session) { setEstado('listo'); return; }
      // Un margen: el token del fragmento puede seguir procesándose. Y toda
      // espera necesita un plazo, o la pantalla se queda en «Comprobando…»
      // para siempre cuando el enlace no trae token.
      setTimeout(() => {
        if (vivo) setEstado((e) => (e === 'comprobando' ? 'sin-sesion' : e));
      }, 2500);
    });

    return () => { vivo = false; sub.subscription.unsubscribe(); };
  }, []);

  const problema = (): string | null => {
    if (password.length < MINIMO) return `La contraseña debe tener al menos ${MINIMO} caracteres.`;
    if (password !== repetida) return 'Las dos contraseñas no coinciden.';
    return null;
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    const malo = problema();
    if (malo) { toast.error(malo); return; }

    setGuardando(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        // El más común: el enlace caducó mientras escribía.
        const caducado = /expired|invalid|token/i.test(error.message);
        toast.error(caducado
          ? 'El enlace caducó. Pide uno nuevo desde «¿Olvidaste tu contraseña?».'
          : error.message);
        return;
      }

      setEstado('guardado');
      // Se cierra la sesión temporal: debe entrar con la contraseña nueva,
      // que es lo que confirma que la recuerda.
      await supabase.auth.signOut();
      setTimeout(() => navigate('/login', { replace: true }), 2600);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Marco>
      {estado === 'comprobando' && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Comprobando el enlace…
        </p>
      )}

      {estado === 'sin-sesion' && (
        <div className="flex gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div>
            <h2 className="mb-1.5 font-display font-semibold">El enlace no es válido</h2>
            <p className="mb-5 text-sm text-muted-foreground">
              Puede que haya caducado —duran una hora— o que ya se haya usado.
              Los enlaces sirven una sola vez.
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="min-h-[44px] text-sm font-medium text-primary hover:underline"
            >
              Pedir uno nuevo
            </button>
          </div>
        </div>
      )}

      {estado === 'guardado' && (
        <div className="py-3 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Check className="h-6 w-6 text-primary" />
          </div>
          <h2 className="mb-1.5 font-display font-semibold">Contraseña actualizada</h2>
          <p className="text-sm text-muted-foreground">
            Ya puedes entrar con la nueva. Te llevamos al inicio…
          </p>
        </div>
      )}

      {estado === 'listo' && (
        <>
          <h2 className="mb-1.5 font-display font-semibold">Elige una contraseña nueva</h2>
          <p className="mb-6 text-sm text-muted-foreground">
            Al menos {MINIMO} caracteres. Usa una que no tengas en otro sitio.
          </p>

          <form onSubmit={guardar} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="nueva" className="text-sm font-medium">Contraseña nueva</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                <input
                  id="nueva"
                  type={ver ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  required
                  placeholder="••••••••"
                  className="min-h-[48px] w-full rounded-xl border border-input bg-background pl-11 pr-11 text-[15px] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-ring/25"
                />
                <button
                  type="button"
                  onClick={() => setVer((v) => !v)}
                  aria-label={ver ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  {ver ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="repetida" className="text-sm font-medium">Repítela</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                <input
                  id="repetida"
                  type={ver ? 'text' : 'password'}
                  value={repetida}
                  onChange={(e) => setRepetida(e.target.value)}
                  autoComplete="new-password"
                  required
                  placeholder="••••••••"
                  className="min-h-[48px] w-full rounded-xl border border-input bg-background pl-11 pr-4 text-[15px] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-ring/25"
                />
              </div>
            </div>

            {/* El aviso sale mientras escribe, no al enviar: corregir a tiempo
                molesta menos que un error después de pulsar el botón. */}
            {repetida.length > 0 && problema() && (
              <p className="text-xs text-amber-700">{problema()}</p>
            )}

            <button
              type="submit"
              disabled={guardando || Boolean(problema())}
              className="min-h-[48px] w-full rounded-xl bg-primary text-[15px] font-semibold text-primary-foreground shadow-soft transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {guardando ? 'Guardando…' : 'Guardar contraseña'}
            </button>
          </form>
        </>
      )}
    </Marco>
  );
}

/** El marco con la marca, común a los cuatro estados. */
function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5 py-10">
      <div className="w-full max-w-md">
        <div className="mb-7 flex items-center gap-3">
          <GroomingProLogo className="h-11 w-11" />
          <div>
            <p className="font-display font-semibold">Grooming ERP</p>
            <p className="text-xs text-muted-foreground">Peluquería, hotel y daycare</p>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-7 shadow-soft">
          {children}
        </div>
      </div>
    </div>
  );
}
